import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import mapboxgl from 'mapbox-gl';
import { isEqual } from 'lodash-es';

import ActionsCancel from '@strava/icons/ActionsCancelNormalXsmall';

import clsx from '../clsx';
import Button from '../Button';

import useMap from './useMap';
import { latLngToLngLat } from './utils';

import styles from './Popup.scss';

const Popup = (props) => {
  const {
    className: classNameProp,
    latLng,
    children,
    ...mapboxPopupOptions
  } = props;
  const map = useMap();

  const prevPropsRef = useRef(props);

  // container used for the portal that will render React components inside of mapboxgl.Popup
  const container = useMemo(() => document.createElement('div'), []);

  // Create mapbox popup instance
  const popup = useMemo(
    () => {
      const popupInstance = new mapboxgl.Popup({
        ...mapboxPopupOptions,
        className: clsx(styles.popup, classNameProp),
        // Hide default close button in favor of our custom button with Strava icon
        closeButton: false
      });

      popupInstance.setLngLat(latLngToLngLat(latLng));

      return popupInstance;
    },
    // As we only want to create mapboxgl.Popup once and as we manually update it's props/options,
    // we intentionally have empty dependency array and disabled the eslint rule here.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(
    () => {
      const handleOpen = (e) => {
        prevPropsRef.current.onOpen(e);
      };
      const handleClose = (e) => {
        prevPropsRef.current.onClose(e);
      };

      popup.on('open', handleOpen);
      popup.on('close', handleClose);
      popup.setDOMContent(container);
      popup.addTo(map);

      return () => {
        popup.off('open', handleOpen);
        popup.off('close', handleClose);
        popup.remove();
      };
    },
    // We only want to attach events once on initial render and as popup will not change in the
    // lifespan of this component we intentionally have empty dependency array and disabled the
    // eslint rule here.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  //
  // Update the className property
  //
  const prevClassNameString = clsx(popup.options.className);
  const nextClassNameString = clsx(styles.popup, classNameProp);

  if (prevClassNameString !== nextClassNameString) {
    const prevClassNames = prevClassNameString.split(/\s+/);
    const nextClassNames = nextClassNameString.split(/\s+/);

    prevClassNames.forEach((className) => {
      if (className && !nextClassNames.includes(className)) {
        popup.removeClassName(className);
      }
    });

    nextClassNames.forEach((className) => {
      if (className && !prevClassNames.includes(className)) {
        popup.addClassName(className);
      }
    });
  }

  //
  // Update the geographical location of the anchor property, and moves the popup to it.
  //
  const [lat, lng] = latLng;
  if (popup.getLngLat().lat !== lat || popup.getLngLat().lng !== lng) {
    popup.setLngLat(latLngToLngLat(latLng));
  }

  //
  // Update the maximum width property. This is setting the CSS property max-width.
  //
  if (popup.getMaxWidth() !== mapboxPopupOptions.maxWidth) {
    popup.setMaxWidth(mapboxPopupOptions.maxWidth);
  }

  //
  // Update the offset property
  //
  if (!isEqual(prevPropsRef.current.offset, mapboxPopupOptions.offset)) {
    popup.setOffset(mapboxPopupOptions.offset);
  }

  // This needs to be at the bottom so that we make sure prevProps gets updates after all the checks
  // and updates have been performed.
  prevPropsRef.current = props;

  return createPortal(
    <>
      {mapboxPopupOptions.closeButton && (
        <Button
          type="button"
          variant="icon"
          className={styles.closeBtn}
          // This is mimicking what mapbox-gl-js native popup does when close button is clicked
          onClick={() => popup.remove()}
        >
          <ActionsCancel height={9} width={9} />
        </Button>
      )}
      {children}
    </>,
    container
  );
};

Popup.propTypes = {
  /**
   * A string indicating the part of the popup that should be positioned closest to the coordinate.
   * Options are 'center', 'top', 'bottom', 'left', 'right', 'top-left', 'top-right',
   * 'bottom-left', and 'bottom-right'. If unset, the anchor will be dynamically set to ensure the
   * popup falls within the map container with a preference for 'bottom'.
   */
  anchor: PropTypes.oneOf([
    'center',
    'top',
    'bottom',
    'left',
    'right',
    'top-left',
    'top-right',
    'bottom-left',
    'bottom-right'
  ]),

  /**
   * CSS class names to add to popup container
   */
  className: PropTypes.string,

  /**
   * If `true`, a close button will appear in the top right corner of the popup.
   */
  closeButton: PropTypes.bool,

  /**
   * If `true`, the popup will close when the map is clicked.
   */
  closeOnClick: PropTypes.bool,

  /**
   * If `true`, the popup will close when the map moves.
   */
  closeOnMove: PropTypes.bool,

  /**
   * If `true`, the popup will try to focus the first focusable element inside the popup.
   */
  focusAfterOpen: PropTypes.bool,

  /**
   * A string that sets the CSS property of the popup's maximum width (for example, '300px').
   * To ensure the popup resizes to fit its content, set this property to 'none'.
   */
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * A pixel offset applied to the popup's location specified as:
   * - a single number specifying a distance from the popup's location
   * - a PointLike specifying a constant offset
   * - an object of Points specifying an offset for each anchor position
   * Negative offsets indicate left and up.
   */
  offset(props, propName, componentName) {
    const isNumber = typeof props[propName] === 'number';
    const isObject =
      typeof props[propName] === 'object' && props[propName] !== null;
    const isPoint = props[propName] instanceof mapboxgl.Point;
    const isTwoNumArray =
      Array.isArray(props[propName]) &&
      props[propName].length === 2 &&
      props[propName].every((v) => typeof v === 'number');

    if (
      props[propName] &&
      !(isNumber || isObject || isPoint || isTwoNumArray)
    ) {
      return new Error(
        `Invalid prop \`${propName}\` supplied to \`${componentName}\`, ` +
          `expected ` +
          `a single number specifying a distance from the popup's location or ` +
          `a PointLike specifying a constant offset or ` +
          `an object of Points specifying an offset for each anchor position, ` +
          `but got \`${props[propName]}\`.`
      );
    }

    return null;
  },

  /**
   * Fired when the popup is opened manually or programmatically.
   */
  onOpen: PropTypes.func,

  /**
   * Fired when the popup is closed manually or programmatically.
   */
  onClose: PropTypes.func,

  //
  // Custom props that are not natively supported by mapbox-gl-js
  //

  /**
   * Array of two numbers representing latitude and longitude.
   */
  latLng(props, propName, componentName) {
    const isTwoNumArray =
      Array.isArray(props[propName]) &&
      props[propName].length === 2 &&
      props[propName].every((v) => typeof v === 'number');

    if (!isTwoNumArray) {
      return new Error(
        `Invalid prop \`${propName}\` supplied to \`${componentName}\`, ` +
          `expected an array of two numbers [latitude, longitude], ` +
          `but got \`${props[propName]}\`.`
      );
    }

    return null;
  },

  children: PropTypes.node
};

Popup.defaultProps = {
  anchor: undefined,
  className: undefined,
  closeButton: true,
  closeOnClick: true,
  closeOnMove: false,
  focusAfterOpen: true,
  offset: undefined,
  maxWidth: '240px',
  // Events
  onOpen: () => {},
  onClose: () => {},
  // Custom props that are not natively supported by mapbox-gl-js
  children: null
};

export default Popup;
